import { useEffect, useRef, useState, type KeyboardEvent } from "react";

// The popover must survive the pointer's travel across the gap between the
// trigger and the box — an instant close on trigger-leave would dismiss the
// box before it can be reached.
const CLOSE_DELAY_MS = 150;

/**
 * Controlled open-state for hover popovers: open while the cursor is over (or
 * keyboard focus is on) the trigger OR the popover box, closed shortly after
 * leaving both. Click on the trigger toggles; Escape closes. Spread
 * `triggerProps` on the trigger element and `contentProps` on the popover
 * content.
 */
export function useHoverOpen() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openNow = () => {
    cancelPendingClose();
    setOpen(true);
  };

  const closeAfterDelay = () => {
    cancelPendingClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => cancelPendingClose, []);

  return {
    open,
    triggerProps: {
      // Triggers are often non-focusable elements (icons, spans); tabIndex
      // puts them in the Tab order so onFocus/onBlur can fire at all.
      tabIndex: 0,
      onMouseEnter: openNow,
      onMouseLeave: closeAfterDelay,
      onFocus: openNow,
      onBlur: closeAfterDelay,
      onClick: () => setOpen((wasOpen) => !wasOpen),
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          cancelPendingClose();
          setOpen(false);
        }
      },
    },
    contentProps: {
      onMouseEnter: openNow,
      onMouseLeave: closeAfterDelay,
    },
  };
}
